import { useFetchCompletedForm } from '@/api/hooks/useFetchCompletedForm'
import { SubmittedAnswers } from '@/components/FormsPage/SubmittedAnswers'
import { formatSubmittedOn } from '@/components/FormsPage/formatting'
import ProtectedPage from '@/components/ProtectedPage'
import { Button, ButtonText } from '@/ui/core/button'
import { FlowHeader } from '@/ui/core/flow-header'
import { Header } from '@/ui/core/header'
import { PageShell } from '@/ui/core/page-shell'
import { Screen } from '@/ui/core/screen'
import { Skeleton } from '@/ui/core/skeleton'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { router, useLocalSearchParams } from 'expo-router'
import { useMemo } from 'react'
import Images from '#/assets/images/image-map'

const goBack = () => (router.canGoBack() ? router.back() : router.replace('/care'))

/**
 * A submitted form as its own page, for a deep link or a push from elsewhere. On Care the same
 * record steps into the forms sheet instead, and both draw the answers from one component.
 */
export default function CompletedFormScreen() {
	const { id } = useLocalSearchParams<{ id: string }>()
	const formId = useMemo(() => Number.parseInt(id ?? '', 10), [id])
	const { completedForm, isLoading, isError } = useFetchCompletedForm(formId)

	return (
		<ProtectedPage>
			<Screen preset="fixed" className="flex flex-1" safeAreaEdges={['top']}>
				<FlowHeader onBack={goBack} />
				<PageShell photo={Images.BrandPhoto}>
					<Skeleton isLoading={isLoading} className="h-24 w-full">
						{isError || !completedForm ? (
							<VStack gap={24}>
								<VStack gap={12}>
									<Header variant="h2">This form is not available</Header>
									<Text>
										It may belong to another account, or something went wrong reading it.
									</Text>
								</VStack>
								<Button variant="secondary" fullWidth={false} onPress={goBack}>
									<ButtonText>Back to Care</ButtonText>
								</Button>
							</VStack>
						) : (
							<VStack gap={32} className="pt-2">
								{/* The name, then the line saying when it went in, as a row says it. */}
								<VStack gap={10}>
									<Header variant="h2">{completedForm.name}</Header>
									<Text size="body1" tone="soft">
										{`Submitted ${formatSubmittedOn(completedForm.submitted)}`}
									</Text>
								</VStack>
								<SubmittedAnswers entries={completedForm.entries} />
							</VStack>
						)}
					</Skeleton>
				</PageShell>
			</Screen>
		</ProtectedPage>
	)
}
